import { useEffect, useRef, useState } from 'react';
import { usePaywallGate } from '../../../utils/paywall';
import {
  CUSTOMER_EXPORT_COLUMNS,
  OPTIONAL_EXPORT_KEYS,
  exportCustomersXlsx,
  readSelectedOptionalKeys,
  resolveExportColumns,
  saveSelectedOptionalKeys,
} from '../utils/exportXlsx';
import type { Customer } from '../hooks/useCustomers';

interface CustomerExportButtonProps {
  /** 当前列表页看到的那批客户（含筛选结果），导出与所见必须同源 */
  customers: Customer[];
  disabled?: boolean;
}

/**
 * 客户导出：一个入口，点开就是「选字段 + 导出」，选完直接点导出即出文件。
 *
 * 此前是页头两个按钮（「导出设置」开弹窗勾字段、「导出 Excel」出文件），要来回开合
 * 两次才能完成一次导出，而且勾完还得先关闭弹窗才看得见按钮。收在一处之后，
 * 勾选与导出读的是同一份即时状态，也就不存在「改了设置但忘了它是分开生效」的困惑。
 */
export default function CustomerExportButton({ customers, disabled }: CustomerExportButtonProps) {
  const gate = usePaywallGate();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(readSelectedOptionalKeys);
  const [justExported, setJustExported] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // 点面板外任意处收起：下拉面板没有遮罩，不给这条路用户就只能靠「取消」按钮退出
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const columns = resolveExportColumns(selected);
  const canExport = customers.length > 0 && !disabled;

  const toggle = (key: string, checked: boolean) => {
    // 以 OPTIONAL_EXPORT_KEYS 为序重排：勾选的先后不影响列顺序
    const picked = new Set(selected);
    setSelected(OPTIONAL_EXPORT_KEYS.filter((k) => (k === key ? checked : picked.has(k))));
  };

  const handleExport = async () => {
    if (!canExport) return;
    // 会员墙判在点导出的这一刻，不锁面板、不画锁：会员状态是异步拉的，
    // 加载完成前据此判会误伤付费用户——与订单页/详情抽屉共用同一个 gate。
    if (!await gate('exportCsv', '导出 Excel')) return;
    exportCustomersXlsx(customers, undefined, selected);
    saveSelectedOptionalKeys(selected);
    setOpen(false);
    setJustExported(true);
    setTimeout(() => setJustExported(false), 2000);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        disabled={disabled || customers.length === 0}
        title={customers.length === 0 ? '暂无可导出的客户' : `选择导出字段并导出当前 ${customers.length} 位客户`}
        className={`flex items-center gap-2 px-3 md:px-4 py-2.5 text-sm font-medium rounded-xl border transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50
          ${justExported
            ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
            : open
              ? 'bg-teal-50 text-teal-600 border-teal-200'
              : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300 hover:text-teal-600'
          }`}
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className={justExported ? 'ri-check-line' : 'ri-download-2-line'} />
        </div>
        <span className="hidden sm:inline">{justExported ? '已导出' : '导出 Excel'}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] z-40 w-[320px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-medium text-gray-900">选择导出字段</p>
            <p className="text-xs text-gray-400 mt-0.5">
              固定列每份都带，勾选只对本机记住
            </p>
          </div>

          <div className="max-h-[50vh] overflow-y-auto px-3 py-3 space-y-1.5">
            {CUSTOMER_EXPORT_COLUMNS.map((column) => (
              <div key={column.key}>
                {column.optional ? (
                  <label className="flex items-center gap-3 px-2.5 py-2 rounded-lg border border-gray-200 hover:border-teal-300 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={selected.includes(column.key)}
                      onChange={(e) => toggle(column.key, e.target.checked)}
                      className="accent-teal-500 cursor-pointer shrink-0"
                    />
                    <span className="text-sm text-gray-700">{column.header}</span>
                  </label>
                ) : (
                  <div className="flex items-center gap-3 px-2.5 py-2 rounded-lg bg-gray-50 border border-gray-100">
                    <div className="w-[18px] h-[18px] flex items-center justify-center shrink-0 text-gray-300">
                      <i className="ri-lock-2-line text-xs" />
                    </div>
                    <span className="text-sm text-gray-700">{column.header}</span>
                    <span className="ml-auto text-xs text-gray-400 shrink-0">固定列</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="px-3 py-3 border-t border-gray-100 space-y-2.5">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelected([...OPTIONAL_EXPORT_KEYS])}
                disabled={selected.length === OPTIONAL_EXPORT_KEYS.length}
                className="px-2.5 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:border-teal-300 hover:text-teal-600 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
              >
                全选
              </button>
              <button
                type="button"
                onClick={() => setSelected([])}
                disabled={selected.length === 0}
                className="px-2.5 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:border-teal-300 hover:text-teal-600 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
              >
                清空
              </button>
              <span className="text-xs text-gray-400 ml-auto">
                共 {columns.length} 列 · {customers.length} 位客户
              </span>
            </div>
            <button
              type="button"
              onClick={() => { void handleExport(); }}
              disabled={!canExport}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-download-2-line" />
              </div>
              立即导出 Excel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
